import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../../components/SeoMeta';
import CmsImage from '../../components/CmsImage';
import { fetchCollection, fetchLocalizedCollection, getLocalized } from '../../lib/cms';

export default function PartnersPageEn() {
  const [cmsPartners, setCmsPartners] = useState([]);
  const [pageData, setPageData] = useState(null);

  useEffect(() => {
    fetchLocalizedCollection('sider', 'en').then((items) => {
      const getSlugVal = (it) =>
        typeof it?.slug === 'object'
          ? it.slug.da || it.slug._ || it.slug.en || Object.values(it.slug)[0]
          : it?.slug;
      const match = (items || []).find(
        (s) => getSlugVal(s) === 'pmb-partnere' || getSlugVal(s) === 'partners'
      );
      if (match) setPageData(match);
    });

    fetchLocalizedCollection('partnere_leverandoerer', 'en').then((items) => {
      if (items && items.length) {
        const filtered = items.filter((i) => {
          const kat = typeof i.kategori === 'object' ? getLocalized(i.kategori, 'en') : i.kategori;
          const hasTerm =
            i.terms &&
            i.terms['partner-kategori'] &&
            i.terms['partner-kategori'].includes('partner');
          return kat === 'Partner' || kat === 'partner' || hasTerm;
        });

        const uniqueMap = new Map();
        for (const item of filtered) {
          const rawName =
            typeof item.navn === 'object' ? getLocalized(item.navn, 'en') : item.navn;
          const key = (rawName || '').trim().toLowerCase();
          if (!key) continue;

          const existing = uniqueMap.get(key);
          if (!existing) {
            uniqueMap.set(key, item);
          } else {
            const itemTime = item.updatedAt ? new Date(item.updatedAt).getTime() : 0;
            const existingTime = existing.updatedAt ? new Date(existing.updatedAt).getTime() : 0;
            if (itemTime > existingTime) {
              uniqueMap.set(key, item);
            }
          }
        }

        const sorted = Array.from(uniqueMap.values()).sort(
          (a, b) => (a.sortering || 0) - (b.sortering || 0)
        );

        if (sorted.length > 0) {
          setCmsPartners(
            sorted.map((p) => ({
              name: typeof p.navn === 'object' ? getLocalized(p.navn, 'en') : p.navn,
              desc: typeof p.beskrivelse === 'object' ? getLocalized(p.beskrivelse, 'en') : p.beskrivelse,
              img: Array.isArray(p.logo) ? p.logo[0] : p.logo,
              url: p.hjemmeside,
            }))
          );
        }
      }
    });
  }, []);

  const defaultPartners = [
    {
      name: 'Fredko Group',
      desc: 'Fredko Group provides deep technical know-how, premium tooling components, and delivery reliability. Together, we engineer specialized solutions for the Nordic metal- and sheet-working industry.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/fredko-final-1.webp',
      url: 'https://www.fredko.com/',
    },
    {
      name: 'PM Service',
      desc: 'PM Service delivers on-site maintenance, installation, and technical field support, ensuring our industrial customers experience maximum machine uptime and swift response times.',
      img: 'https://assets.auxodev.dk/assets/28e87dfb-16e7-4c8a-a569-3940d683c543/pm-service-final-1.webp',
      url: 'https://pmservice.se/',
    },
  ];

  const partners = cmsPartners.length > 0 ? cmsPartners : defaultPartners;

  const title = pageData?.titel || 'Strong Partners – Stronger Solutions';
  const badge = pageData?.undertitel || 'PMB Partners';
  const intro =
    pageData?.intro ||
    'At PMB, we believe the best industrial results are achieved through the synergy of deep experience, specialized technical competence, and shared core values.';
  const seoTitle =
    pageData?.seo_titel || 'PMB Partners – Strategic Collaborations & Industrial Solutions';
  const metaDesc =
    pageData?.meta_beskrivelse ||
    'PMB collaborates with leading manufacturers and specialized engineering partners within presses, coil lines, and lubrication. Together we deliver reliable industrial operations.';

  return (
    <>
      <SeoMeta
        title={seoTitle}
        description={metaDesc}
      />

      {/* Breadcrumb */}
      <div className="bg-stone-100 border-b border-stone-200 py-3 px-4 sm:px-6 lg:px-8 text-xs text-stone-600">
        <div className="max-w-7xl mx-auto flex items-center gap-2">
          <Link to="/en" className="hover:text-[#006368] transition">
            Home
          </Link>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="text-stone-500">About us</span>
          <i className="fa-solid fa-chevron-right text-[10px] text-stone-400" />
          <span className="font-semibold text-stone-900">PMB Partners</span>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-[#1a2024] text-white py-16 sm:py-24 border-b border-stone-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="inline-block px-3 py-1 rounded bg-[#006368] text-white text-xs font-bold uppercase tracking-wider mb-4">
              {badge}
            </span>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {title}
            </h1>
            <p className="mt-4 text-base sm:text-lg text-stone-300 leading-relaxed">
              {intro}
            </p>
            <p className="mt-2 text-sm sm:text-base text-stone-400 leading-relaxed">
              That is why we collaborate closely with dedicated partners who mirror our commitment to quality, uptime, and continuous innovation.
            </p>
          </div>
        </div>
      </section>

      {/* Partners Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {partners.map((p, i) => (
              <div
                key={i}
                className="bg-white rounded-lg border border-stone-200 overflow-hidden shadow-sm hover:border-[#006368] transition duration-200 flex flex-col justify-between p-8"
              >
                <div>
                  <div className="h-52 sm:h-60 w-full flex items-center justify-center p-6 sm:p-8 bg-[#121619] rounded-lg border border-stone-800 mb-6 shadow-inner">
                    <CmsImage
                      src={p.img}
                      alt={p.name}
                      className="h-36 sm:h-44 w-auto max-w-[85%] object-contain filter brightness-105"
                      width={600}
                    />
                  </div>
                  <h3 className="text-2xl font-bold text-stone-900">{p.name}</h3>
                  <p className="mt-3 text-stone-700 text-sm sm:text-base leading-relaxed">
                    {p.desc}
                  </p>
                </div>

                <div className="mt-8 pt-6 border-t border-stone-100 flex items-center justify-between">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded bg-[#006368] hover:bg-[#004d51] text-white text-xs font-semibold tracking-wide transition shadow-2xs"
                  >
                    <span>Visit {p.name}</span>
                    <i className="fa-solid fa-arrow-up-right-from-square text-[11px]" />
                  </a>
                  <span className="text-xs text-stone-400 font-medium">Official PMB Partner</span>
                </div>
              </div>
            ))}
          </div>

          {/* Statement box */}
          <div className="mt-16 p-8 sm:p-12 rounded-lg bg-stone-50 border border-stone-200 max-w-4xl mx-auto text-center space-y-4">
            <h3 className="text-xl sm:text-2xl font-bold text-stone-900">
              Together we engineer solutions that endure
            </h3>
            <p className="text-stone-700 text-base leading-relaxed max-w-2xl mx-auto">
              Together we create solutions that do more than just keep machinery turning – they elevate complete production workflows to perform better, longer, and more productively.
            </p>
            <p className="text-[#006368] font-semibold text-base leading-relaxed max-w-2xl mx-auto">
              A partnership with PMB is not just about equipment. It centers on people, know-how, and a shared dedication to delivering genuine quality every single day.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
